import { useMemo, useRef } from 'react'
import clsx from 'clsx'
import { ListProps as ListComponentProps } from 'react-virtualized/dist/commonjs/List'
import { toNumber } from 'utils/Hooks'
import { FixedSizeListProps, ListConfigType } from '../index'
import stylesGlobal from 'stylesGlobal.module.scss'

const DEFAULT_COLUMN_WIDTH = 0

type ListProps<Item> = Pick<
  ListComponentProps,
  'isScrolling' | 'onScroll' | 'overscanRowCount' | 'scrollTop' | 'width'
> &
  Pick<
    FixedSizeListProps<Item>,
    'calculateCellHeight' | 'data' | 'debug' | 'renderItem' | 'noRowsRenderer'
  > & {
    config: ListConfigType
    onBottom?: Function
  }

function List<Item>(props: ListProps<Item>) {
  const { config, data, width, renderItem } = props

  const itemRef = useRef<HTMLDivElement>(null)
  const gutter = toNumber(config?.gutter ?? 0)

  const paddingLeft = toNumber(config.paddingLeft || config.paddingX) || 0
  const paddingRight = toNumber(config.paddingRight || config.paddingX) || 0
  const totalPaddingHorizontal =
    paddingLeft || paddingRight ? toNumber(paddingLeft) + toNumber(paddingRight) : 0

  const useMaxWidth = config?.maxWidth && width > config?.maxWidth
  const adjustedWidth =
    (useMaxWidth ? toNumber(config?.maxWidth) : width) -
    (!useMaxWidth ||
    (config?.maxWidth && useMaxWidth && width - config?.maxWidth < totalPaddingHorizontal)
      ? totalPaddingHorizontal
      : 0)

  const columnCount = config?.columns
    ? toNumber(config?.columns)
    : config?.defaultColumnWidth
    ? Math.floor(
        adjustedWidth / (toNumber(config?.defaultColumnWidth) + gutter || DEFAULT_COLUMN_WIDTH)
      )
    : 1

  const adjustedColumnWidth = adjustedWidth ? Math.floor((adjustedWidth + gutter) / columnCount) : 0

  const ItemsComponent = useMemo(() => {
    const components: any = []

    data?.map((item: Item, index: number) => {
      const paddingAdjustment = config?.gutter || undefined

      return components.push(
        <div
          ref={index === 0 ? itemRef : undefined}
          key={index}
          style={{
            margin: 0,
            flexBasis: adjustedColumnWidth,
            flexGrow: 0,
            maxWidth: adjustedColumnWidth,
            height: config?.rowHeight || undefined,
            paddingTop: paddingAdjustment,
            paddingLeft: paddingAdjustment
          }}
        >
          {renderItem({
            item,
            index,
            width: adjustedColumnWidth - (config?.gutter || 0) || 0,
            height: (config?.rowHeight || 0) - (config?.gutter || 0) || undefined
          })}
        </div>
      )
    })

    return components
  }, [adjustedColumnWidth, data, renderItem, config])

  const hasContainerPadding =
    config?.paddingTop ||
    config?.paddingRight ||
    config?.paddingLeft ||
    config?.paddingBottom ||
    config?.paddingX ||
    config?.paddingY

  if (useMaxWidth || (hasContainerPadding && gutter))
    return (
      <div
        className={
          useMaxWidth
            ? clsx(stylesGlobal.DisplayFlex, stylesGlobal.JustifyContentCenter)
            : undefined
        }
        style={
          hasContainerPadding
            ? {
                paddingTop: toNumber(config?.paddingTop || config?.paddingY) || undefined,
                paddingLeft: toNumber(config?.paddingLeft || config?.paddingX) || undefined,
                paddingRight: toNumber(config?.paddingRight || config?.paddingX) || undefined,
                paddingBottom: toNumber(config?.paddingBottom || config?.paddingY) || undefined
              }
            : undefined
        }
      >
        <div
          className={clsx(stylesGlobal.DisplayFlex, stylesGlobal.FlexWrap)}
          style={{
            maxWidth: useMaxWidth ? adjustedWidth + (gutter || 0) : undefined,
            width: gutter ? `calc(100% + ${gutter}px)` : undefined,
            marginTop: gutter ? -gutter : undefined,
            marginLeft: gutter ? -gutter : undefined
          }}
        >
          {ItemsComponent}
        </div>
      </div>
    )

  return (
    <div
      className={clsx(stylesGlobal.DisplayFlex, stylesGlobal.FlexWrap)}
      style={
        hasContainerPadding && !gutter
          ? {
              maxWidth: adjustedWidth,
              paddingTop: toNumber(config?.paddingTop || config?.paddingY),
              paddingLeft: toNumber(config?.paddingLeft || config?.paddingX),
              paddingRight: toNumber(config?.paddingRight || config?.paddingX),
              paddingBottom: toNumber(config?.paddingBottom || config?.paddingY)
            }
          : gutter && !hasContainerPadding
          ? {
              maxWidth: adjustedWidth + (gutter || 0),
              width: `calc(100% + ${gutter}px)`,
              marginTop: -gutter,
              marginLeft: -gutter
            }
          : undefined
      }
    >
      {ItemsComponent}
    </div>
  )
}

export default List
